import React from "react";
import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

const Contact = () => {
  const contactItems = [
    {
      label: "Executive Director",
      value: "(587) 700-5985",
      type: "phone",
    },
    {
      label: "Direct Line",
      value: "(403) 984-6091",
      type: "phone",
    },
    {
      label: "Email",
      value: "info@ethiocare.ca",
      type: "email",
    },
  ];

  const PhoneIcon = () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const MailIcon = () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 6H20C20.55 6 21 6.45 21 7V17C21 17.55 20.55 18 20 18H4C3.45 18 3 17.55 3 17V7C3 6.45 3.45 6 4 6Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M21 7L12 13L3 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <main className="-mt-10 min-h-screen flex items-center bg-[#F3FBFA] dark:bg-[#101010] text-[#163C3B] dark:text-white font-sans overflow-hidden">
      <section className="relative w-full px-6 sm:px-10 lg:px-16 py-16 md:py-20">
        <div className="relative max-w-6xl mx-auto">
          {/* HEADER */}
          <motion.div
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.75,
              ease,
            }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <p className="text-xs sm:text-sm font-bold tracking-[0.28em] uppercase text-[#8F8120] dark:text-[#CDB937] mb-4">
              Get in Touch
            </p>

            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-5 leading-tight tracking-[-0.035em] text-[#163C3B] dark:text-white"
              style={{
                textShadow:
                  "0 2px 3px rgba(17,63,61,0.14), 0 8px 22px rgba(17,63,61,0.05)",
              }}
            >
              Contact EthioCare
            </h1>

            <p className="text-base md:text-lg text-[#577371] dark:text-gray-300 leading-relaxed">
              We are here to listen, support, and connect community members with
              the right resources.
            </p>
          </motion.div>

          {/* EMERGENCY NOTICE */}
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.12,
              duration: 0.7,
              ease,
            }}
            className="mb-7 border-l-4 border-red-500 bg-red-50 dark:bg-red-500/10 px-6 py-4 text-left"
          >
            <h2 className="text-base font-bold text-red-700 dark:text-red-300 mb-1">
              Emergency Support
            </h2>

            <p className="text-sm text-red-700/90 dark:text-red-200/90 leading-relaxed">
              If you or someone else is in immediate danger, call 911. For
              non-emergency community support, please use the contact information
              below.
            </p>
          </motion.div>

          {/* CONTACT CARDS */}
          <motion.div
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.22,
              duration: 0.7,
              ease,
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
          >
            {contactItems.map((item, index) => (
              <motion.article
                key={item.label}
                initial={{
                  opacity: 0,
                  y: 22,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: 0.3 + index * 0.1,
                  duration: 0.6,
                  ease,
                }}
                className="group border border-[#C7DEDA] dark:border-white/10 bg-[#EAF6F4] dark:bg-[#171717] p-6 text-left shadow-[0_14px_35px_rgba(32,82,78,0.07)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(32,82,78,0.12)]"
              >
                <div className="mb-5 flex items-center gap-3 text-[#8F8120] dark:text-[#CDB937]">
                  {item.type === "phone" ? <PhoneIcon /> : <MailIcon />}

                  <p className="text-xs uppercase tracking-[0.18em] font-bold">
                    {item.label}
                  </p>
                </div>

                {item.type === "phone" ? (
                  <a
                    href={`tel:${item.value.replace(/[^\d+]/g, "")}`}
                    className="text-xl sm:text-2xl font-extrabold text-[#163C3B] dark:text-white break-words transition-colors group-hover:text-[#817416] dark:group-hover:text-[#CDB937]"
                  >
                    {item.value}
                  </a>
                ) : (
                  <a
                    href={`mailto:${item.value}`}
                    className="text-xl sm:text-2xl font-extrabold text-[#163C3B] dark:text-white break-words transition-colors group-hover:text-[#817416] dark:group-hover:text-[#CDB937]"
                  >
                    {item.value}
                  </a>
                )}
              </motion.article>
            ))}
          </motion.div>

          {/* SUPPORT INFO */}
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.52,
              duration: 0.7,
              ease,
            }}
            className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <div className="border-t border-[#A7CBC6] dark:border-zinc-700 pt-6 text-left">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-3">
                Who We Support
              </p>

              <h3
                className="text-xl md:text-2xl font-extrabold mb-3 text-[#163C3B] dark:text-white"
                style={{
                  textShadow: "0 2px 3px rgba(17,63,61,0.08)",
                }}
              >
                Community-centred care
              </h3>

              <p className="text-sm md:text-base text-[#577371] dark:text-gray-300 leading-relaxed">
                EthioCare supports Ethiopian, Eritrean, South Sudanese, African
                newcomer, and Black racialized communities through culturally
                responsive care and community connection.
              </p>
            </div>

            <div className="border-t border-[#A7CBC6] dark:border-zinc-700 pt-6 text-left">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-3">
                How We Can Help
              </p>

              <h3
                className="text-xl md:text-2xl font-extrabold mb-3 text-[#163C3B] dark:text-white"
                style={{
                  textShadow: "0 2px 3px rgba(17,63,61,0.08)",
                }}
              >
                Find the right support
              </h3>

              <p className="text-sm md:text-base text-[#577371] dark:text-gray-300 leading-relaxed">
                Reach out for community support, resource navigation, crisis
                connection, family support, referrals, or general program
                questions.
              </p>
            </div>
          </motion.div>

          {/* BOTTOM MESSAGE */}
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              delay: 0.72,
              duration: 0.7,
            }}
            className="mt-9 pt-6 border-t border-[#B6D3CF] dark:border-zinc-800 text-center"
          >
            <p className="text-sm font-medium text-[#68817F] dark:text-gray-400">
              Serving Calgary and surrounding communities with care, dignity,
              and compassion.
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Contact;